import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

interface SoftWallModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'daily_limit' | 'budget_warning';
  usage?: {
    used: number;
    limit: number;
    plan?: string;
  };
}

const SoftWallModal: React.FC<SoftWallModalProps> = ({ isOpen, onClose, type, usage }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!isOpen) return null;

  const isLimit = type === 'daily_limit';
  const percent = usage ? Math.round((usage.used / usage.limit) * 100) : 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-dark-bg rounded-2xl max-w-sm w-full p-6 relative">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-colors"
        >
          <XMarkIcon className="w-5 h-5 text-light-text-secondary dark:text-dark-text-secondary" />
        </button>

        {/* Icon */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-full mb-3">
            <SparklesIcon className="w-8 h-8 text-primary-500" />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold text-light-text dark:text-dark-text text-center mb-2">
          {isLimit
            ? t('softWall.limitReachedTitle', "You've reached your limit")
            : t('softWall.budgetWarningTitle', "You're running low")}
        </h3>

        {/* Message */}
        <p className="text-light-text-secondary dark:text-dark-text-secondary text-sm text-center mb-4">
          {isLimit
            ? t(
                'softWall.limitReachedMessage',
                "You've used all your messages for today. Upgrade to Plus for unlimited conversations.",
              )
            : t(
                'softWall.budgetWarningMessage',
                "You've used all {{limit}} of your free monthly credits. Upgrade for more — or they'll reset next month.",
                {
                  percent,
                  limit: usage?.limit,
                },
              )}
        </p>

        {/* Usage Bar */}
        {usage && (
          <div className="mb-5">
            <div className="flex justify-between text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1">
              <span>
                {usage.used} / {usage.limit}
              </span>
              <span>{percent}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${percent >= 100 ? 'bg-red-500' : 'bg-yellow-500'}`}
                style={{ width: `${Math.min(percent, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Upgrade Button */}
        <button
          onClick={() => {
            onClose();
            navigate('/subscription');
          }}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 mb-3"
        >
          <SparklesIcon className="w-4 h-4" />
          {t('softWall.upgrade', 'Upgrade Plan')}
        </button>

        {/* Top Up Credits link */}
        <button
          onClick={() => {
            onClose();
            navigate('/credits');
          }}
          className="w-full py-3 bg-light-surface dark:bg-dark-surface hover:bg-gray-100 dark:hover:bg-dark-border text-light-text dark:text-dark-text font-medium rounded-xl transition-colors text-sm"
        >
          {t('softWall.topUp', 'Top Up Credits Instead')}
        </button>

        {/* Dismiss */}
        <button
          onClick={onClose}
          className="w-full mt-2 py-2 text-light-text-secondary dark:text-dark-text-secondary text-sm"
        >
          {t('softWall.maybeLater', 'Maybe later')}
        </button>
      </div>
    </div>
  );
};

export default SoftWallModal;
